import { useI18n } from '@/hooks/useI18n';
import { Flame, Layers, TrendingUp } from 'lucide-react';
import { QuickStat } from '@/components/primitives/QuickStat';
import { Sparkline } from '@/components/primitives/Sparkline';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber, formatPct } from '@/lib/format';
import type { TemplateSummary } from '@/types/api';

function computeMedian(sorted: number[]): number {
  if (sorted.length === 0) return 0;
  const n = sorted.length;
  if ((n & 1) === 1) return sorted[(n - 1) / 2];
  return Math.round((sorted[n / 2 - 1] + sorted[n / 2]) / 2);
}

interface TemplatesKpiRowProps {
  templates: TemplateSummary[];
  isLoading: boolean;
}

export function TemplatesKpiRow({ templates, isLoading }: TemplatesKpiRowProps) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="grid gap-3 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-[128px] w-full rounded-[14px]" />
        ))}
      </div>
    );
  }

  if (templates.length === 0) return null;

  const totalTemplates = templates.length;
  const sortedMedians = templates.map((t) => t.medianViews).sort((a, b) => a - b);
  const medianOfMedians = computeMedian(sortedMedians);
  const viralTemplates = templates.filter((t) => t.viralCount > 0).length;
  const viralShare = totalTemplates > 0 ? viralTemplates / totalTemplates : 0;

  // Top 5 par médiane — indépendant du tri courant de la liste (l'utilisateur peut
  // trier par REEL_COUNT ou autre, la sparkline doit rester "top 5 en performance").
  const topFiveMedians = [...templates]
    .sort((a, b) => b.medianViews - a.medianViews)
    .slice(0, 5)
    .map((t) => t.medianViews);

  return (
    <div className="grid gap-3 md:grid-cols-3">
      <QuickStat
        label={t('Templates détectés', 'Detected templates')}
        hint={t(
          'Groupes de reels avec caption identique (≥ 3 reels)',
          'Groups of reels with matching captions (≥ 3 reels)',
        )}
        value={formatNumber(totalTemplates)}
        chart={
          <div className="flex items-center gap-1.5 text-[11px] text-text-3">
            <Layers className="h-3 w-3 text-violet" />
            {t('Minimum 3 reels requis par template', 'At least 3 reels required per template')}
          </div>
        }
      />
      <QuickStat
        label={t('Médiane globale', 'Overall median')}
        hint={t(
          "Médiane des vues médianes par template — donne l'ordre de grandeur",
          'Median of each template’s median views — indicates the typical scale',
        )}
        value={formatNumber(medianOfMedians)}
        suffix={t('vues', 'views')}
        chart={
          topFiveMedians.length > 1 ? (
            <div className="h-7 w-full text-violet">
              <Sparkline
                data={topFiveMedians}
                stroke="currentColor"
                fill="hsl(var(--violet) / 0.12)"
                strokeWidth={1.5}
              />
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[11px] text-text-3">
              <TrendingUp className="h-3 w-3 text-violet" />
              Top 5 templates
            </div>
          )
        }
      />
      <QuickStat
        label={t('Templates viraux', 'Viral templates')}
        hint={t(
          'Au moins un reel viral flaggé dans le template',
          'At least one reel flagged as viral in the template',
        )}
        value={formatNumber(viralTemplates)}
        suffix={`/ ${formatNumber(totalTemplates)}`}
        subMeta={formatPct(viralShare, { sign: false, digits: 0 })}
        chart={
          <div className="flex items-center gap-1.5 text-[11px] text-text-3">
            <Flame className="h-3 w-3 text-violet-deep" />
            {viralTemplates > 0
              ? t(
                  `${viralTemplates} template${viralTemplates > 1 ? 's ont' : ' a'} déjà percé`,
                  `${viralTemplates} template${viralTemplates > 1 ? 's have' : ' has'} gone viral`,
                )
              : t('Aucun template viral pour le moment', 'No viral templates yet')}
          </div>
        }
      />
    </div>
  );
}
